import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { 
  Map, 
  Route, 
  Navigation, 
  User,
  MapPin,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ArrowLeft,
  ExternalLink,
  LocateFixed,
  Building2,
  MousePointer2,
  Users,
  Hospital,
  ChevronDown,
  ChevronUp,
  Eye,
  Pencil,
  RefreshCw,
  MapPinOff,
  Home
} from 'lucide-react';
import { toast } from 'sonner';
import { GoogleMap, useJsApiLoader, Marker, Polyline, InfoWindow } from '@react-google-maps/api';
import { ORIGEM_LABELS, formatDate, formatCPF } from '../lib/utils';
import { MapPicker } from '../components/ui/map-picker';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Default center (Boa Vista - RR, Brazil)
const DEFAULT_CENTER = { lat: 2.8235, lng: -60.6758 };

const containerStyle = {
  width: '100%',
  height: '100%'
};

// Color schemes for different view modes
const EQUIPE_COLORS = {
  'sem_equipe': '#6B7280', // gray
};

// Origem colors matching Kanban
const ORIGEM_COLORS_MAP = {
  'cardiologia': '#EF4444',  // red
  'paliativo': '#8B5CF6',    // purple
  'clinico': '#3B82F6',      // blue
  'cirurgia': '#F97316',     // orange
  'pacs': '#06B6D4',         // cyan - Emergência
  'hmi': '#F59E0B',          // amber - Maternidade
  'outro': '#6B7280',        // gray
};

// Map hex colors to Google Maps standard marker colors
const getGoogleMarkerUrl = (hexColor) => {
  const colorMap = {
    '#EF4444': 'red',
    '#DC2626': 'red',
    '#3B82F6': 'blue',
    '#2563EB': 'blue',
    '#10B981': 'green',
    '#059669': 'green',
    '#F59E0B': 'yellow',
    '#D97706': 'yellow',
    '#F97316': 'orange',
    '#EA580C': 'orange',
    '#8B5CF6': 'purple',
    '#7C3AED': 'purple',
    '#EC4899': 'pink',
    '#DB2777': 'pink',
    '#6B7280': 'gray',
    '#4B5563': 'gray',
    '#06B6D4': 'ltblue',  // cyan for PACS
    '#0891B2': 'ltblue',
  };
  
  // Find closest color match
  const color = colorMap[hexColor] || 'red';
  return `https://maps.google.com/mapfiles/ms/icons/${color}-dot.png`;
};

const Mapa = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const mapRef = useRef(null);
  
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeResult, setRouteResult] = useState(null);
  const [includeAltas, setIncludeAltas] = useState(false);
  const [geocodingInProgress, setGeocodingInProgress] = useState(false);
  const [activeInfoWindow, setActiveInfoWindow] = useState(null);
  
  // View mode for pin colors
  const [viewMode, setViewMode] = useState('equipe'); // 'equipe' | 'origem' | 'tempo'
  const [equipes, setEquipes] = useState([]);
  
  // Start point options
  const [startPointType, setStartPointType] = useState('base'); // 'base' | 'location' | 'map'
  const [userLocation, setUserLocation] = useState(null);
  const [customStartPoint, setCustomStartPoint] = useState(null);
  const [isSelectingOnMap, setIsSelectingOnMap] = useState(false);
  
  // Técnicos de enfermagem for map markers
  const [tecnicos, setTecnicos] = useState([]);
  const [showTecnicos, setShowTecnicos] = useState(true);
  
  // Base Lifeport from settings
  const [baseLifeport, setBaseLifeport] = useState({
    lat: 2.7865,
    lng: -60.7014,
    endereco: "Rua Manoel Felipe, 608 - Buritis, Boa Vista - RR, CEP 69309-170"
  });

  // Missing location panel state
  const [missingLocationOpen, setMissingLocationOpen] = useState(true);
  const [editAddressModalOpen, setEditAddressModalOpen] = useState(false);
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [selectedMissingPatient, setSelectedMissingPatient] = useState(null);
  const [editAddressData, setEditAddressData] = useState({
    logradouro: '',
    numero: '',
    bairro: '',
    cidade: '',
    uf: 'RR',
    cep: ''
  });
  const [savingAddress, setSavingAddress] = useState(false);
  const [retryingGeocode, setRetryingGeocode] = useState(false);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    language: 'pt-BR'
  });

  useEffect(() => {
    fetchSettings();
    fetchPatients();
    fetchEquipes();
    fetchTecnicos();
  }, []);

  useEffect(() => {
    fetchPatients();
  }, [includeAltas]);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      if (response.data.base_lat && response.data.base_lng) {
        setBaseLifeport({
          lat: response.data.base_lat,
          lng: response.data.base_lng,
          endereco: response.data.base_endereco || "Base Lifeport"
        });
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/mapa/pacientes?include_alta=${includeAltas}`);
      setPatients(response.data);
    } catch (error) {
      console.error('Error fetching patients:', error);
      toast.error('Erro ao carregar pacientes');
    } finally {
      setLoading(false);
    }
  };

  const fetchEquipes = async () => {
    try {
      const response = await api.get('/equipes');
      // API returns { equipes: [...], total_internados, total_equipes } or just array
      let data = response.data;
      if (data && data.equipes && Array.isArray(data.equipes)) {
        data = data.equipes;
      }
      const equipesList = Array.isArray(data) ? data : [];
      setEquipes(equipesList);
    } catch (error) {
      console.error('Error fetching equipes:', error);
      setEquipes([]);
    }
  };

  const fetchTecnicos = async () => {
    try {
      const response = await api.get('/tecnicos-mapa');
      setTecnicos(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching tecnicos:', error);
      setTecnicos([]);
    }
  };

  // Get color for a patient based on view mode
  const getPatientColor = useCallback((patient) => {
    switch (viewMode) {
      case 'equipe':
        if (!patient.equipe_id) return EQUIPE_COLORS['sem_equipe'];
        const equipe = (equipes || []).find(e => e.id === patient.equipe_id);
        return equipe?.cor || '#3B82F6';
      
      case 'origem':
        const origem = patient.origem?.toLowerCase() || 'outro';
        return ORIGEM_COLORS_MAP[origem] || ORIGEM_COLORS_MAP['outro'];
      
      default:
        return '#3B82F6';
    }
  }, [viewMode, equipes]);

  // Generate legend items based on view mode
  const legendItems = useMemo(() => {
    switch (viewMode) {
      case 'equipe':
        const items = (equipes || []).map(e => ({ label: e.nome, color: e.cor || '#3B82F6' }));
        items.push({ label: 'Sem Equipe', color: EQUIPE_COLORS['sem_equipe'] });
        return items;
      
      case 'origem':
        return [
          { label: 'Cardiologia', color: ORIGEM_COLORS_MAP['cardiologia'] },
          { label: 'Paliativo', color: ORIGEM_COLORS_MAP['paliativo'] },
          { label: 'Clínico', color: ORIGEM_COLORS_MAP['clinico'] },
          { label: 'Cirurgia', color: ORIGEM_COLORS_MAP['cirurgia'] },
          { label: 'PACS', color: ORIGEM_COLORS_MAP['pacs'] },
          { label: 'HMI', color: ORIGEM_COLORS_MAP['hmi'] },
          { label: 'Outro', color: ORIGEM_COLORS_MAP['outro'] },
        ];
      
      default:
        return [];
    }
  }, [viewMode, equipes]);

  const handleGeocodeAll = async () => {
    setGeocodingInProgress(true);
    try {
      const response = await api.post('/mapa/geocode-all');
      if (response.data.success > 0) {
        toast.success(`${response.data.success} endereço(s) geocodificado(s) com sucesso!`);
        fetchPatients();
      }
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} endereço(s) não encontrado(s)`);
      }
    } catch (error) {
      toast.error('Erro ao geocodificar endereços');
    } finally {
      setGeocodingInProgress(false);
    }
  };

  // Retry geocoding for missing location patients only
  const handleRetryGeocodeMissing = async () => {
    setRetryingGeocode(true);
    try {
      // Get IDs of patients without location
      const missingIds = patientsWithoutLocation.map(p => p.paciente_id);
      
      const response = await api.post('/mapa/geocode-missing', { patient_ids: missingIds });
      
      if (response.data.success > 0) {
        toast.success(`${response.data.success} endereço(s) localizado(s) com sucesso!`);
        fetchPatients();
      }
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} endereço(s) não puderam ser localizados. Verifique os dados.`);
      }
      if (response.data.success === 0 && response.data.failed === 0) {
        toast.info('Nenhum endereço para processar');
      }
    } catch (error) {
      console.error('Geocode retry error:', error);
      toast.error('Erro ao tentar georreferenciar. Tente novamente.');
    } finally {
      setRetryingGeocode(false);
    }
  };

  // Open address edit modal for a patient
  const handleOpenEditAddress = (patient) => {
    setSelectedMissingPatient(patient);
    setEditAddressData({
      logradouro: patient.logradouro || '',
      numero: patient.numero || '',
      bairro: patient.bairro || '',
      cidade: patient.cidade || 'Boa Vista',
      uf: patient.uf || 'RR',
      cep: patient.cep || ''
    });
    setEditAddressModalOpen(true);
  };

  // Save edited address
  const handleSaveAddress = async () => {
    if (!selectedMissingPatient) return;
    
    setSavingAddress(true);
    try {
      // Update patient address
      const updateData = {
        ...editAddressData,
        nome: selectedMissingPatient.nome // Required by the API
      };
      
      await api.put(`/pacientes/${selectedMissingPatient.paciente_id}`, updateData);
      
      // Try to geocode the new address
      try {
        await api.post(`/pacientes/${selectedMissingPatient.paciente_id}/geocode`);
        toast.success('Endereço atualizado e localização obtida!');
      } catch (geoError) {
        toast.warning('Endereço atualizado, mas não foi possível localizar. Verifique os dados.');
      }
      
      setEditAddressModalOpen(false);
      fetchPatients();
    } catch (error) {
      console.error('Error saving address:', error);
      toast.error('Erro ao salvar endereço. Verifique os dados e tente novamente.');
    } finally {
      setSavingAddress(false);
    }
  };

  // Open map picker for a patient
  const handleOpenMapPicker = (patient) => {
    setSelectedMissingPatient(patient);
    setMapPickerOpen(true);
  };

  // Handle map picker selection
  const handleMapPickerSelect = async (lat, lng, address) => {
    if (!selectedMissingPatient) return;
    
    try {
      // Update patient with new coordinates
      await api.put(`/pacientes/${selectedMissingPatient.paciente_id}/geocode`, {
        lat,
        lng,
        endereco_texto: address
      });
      
      toast.success('Localização definida com sucesso!');
      setMapPickerOpen(false);
      fetchPatients();
    } catch (error) {
      console.error('Error setting location:', error);
      toast.error('Erro ao definir localização. Tente novamente.');
    }
  };

  // Navigate to patient profile
  const handleViewPatient = (patient) => {
    if (patient.internacao_id) {
      navigate(`/pacientes/${patient.paciente_id}/internacao/${patient.internacao_id}`);
    } else {
      navigate(`/pacientes/${patient.paciente_id}`);
    }
  };

  const handlePatientSelect = (patient) => {
    if (!patient.lat || !patient.lng) {
      toast.warning('Este paciente não possui localização geocodificada');
      return;
    }
    
    setSelectedPatients(prev => {
      const exists = prev.find(p => p.internacao_id === patient.internacao_id);
      if (exists) {
        return prev.filter(p => p.internacao_id !== patient.internacao_id);
      }
      return [...prev, patient];
    });
  };

  const getStartPoint = () => {
    switch (startPointType) {
      case 'location':
        return userLocation || baseLifeport;
      case 'map':
        return customStartPoint || baseLifeport;
      default:
        return baseLifeport;
    }
  };

  const handleGetUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            endereco: "Sua localização atual"
          };
          setUserLocation(loc);
          setStartPointType('location');
          toast.success('Localização obtida com sucesso!');
        },
        (error) => {
          toast.error('Não foi possível obter sua localização');
          console.error('Geolocation error:', error);
        }
      );
    } else {
      toast.error('Geolocalização não suportada pelo navegador');
    }
  };

  const handleMapClick = useCallback((e) => {
    if (isSelectingOnMap) {
      setCustomStartPoint({
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
        endereco: "Ponto selecionado no mapa"
      });
      setStartPointType('map');
      setIsSelectingOnMap(false);
      toast.success('Ponto de partida selecionado!');
    }
  }, [isSelectingOnMap]);

  const handleCalculateRoute = async () => {
    if (selectedPatients.length < 1) {
      toast.warning('Selecione pelo menos um paciente');
      return;
    }

    setRouteLoading(true);
    try {
      const startPoint = getStartPoint();
      const response = await api.post('/routes/optimize', {
        origin: {
          lat: startPoint.lat,
          lng: startPoint.lng,
          endereco: startPoint.endereco
        },
        waypoints: selectedPatients.map(p => ({
          lat: p.lat,
          lng: p.lng,
          paciente_id: p.paciente_id,
          paciente_nome: p.nome,
          endereco: p.endereco || p.endereco_formatado
        })),
        optimize: true
      });

      setRouteResult(response.data);
      toast.success('Rota calculada com sucesso!');
    } catch (error) {
      console.error('Error calculating route:', error);
      toast.error('Erro ao calcular rota');
    } finally {
      setRouteLoading(false);
    }
  };

  const handleOpenInGoogleMaps = () => {
    if (routeResult?.google_maps_url) {
      window.open(routeResult.google_maps_url, '_blank');
    }
  };

  const decodePolyline = (encoded) => {
    if (!encoded) return [];
    const poly = [];
    let index = 0, lat = 0, lng = 0;

    while (index < encoded.length) {
      let b, shift = 0, result = 0;
      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      const dlat = ((result & 1) ? ~(result >> 1) : (result >> 1));
      lat += dlat;

      shift = 0;
      result = 0;
      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      const dlng = ((result & 1) ? ~(result >> 1) : (result >> 1));
      lng += dlng;

      poly.push({ lat: lat / 1e5, lng: lng / 1e5 });
    }
    return poly;
  };

  const patientsWithLocation = patients.filter(p => p.lat && p.lng);
  const patientsWithoutLocation = patients.filter(p => !p.lat || !p.lng);

  // Calculate map center - prioritize Base LifePort location (Boa Vista-RR)
  // This ensures the map starts centered on the operational area
  const mapCenter = baseLifeport;
  
  // Calculate appropriate zoom level based on patient distribution
  const getZoomLevel = () => {
    if (patientsWithLocation.length === 0) return 12;
    
    // Check if patients are spread across different regions
    const lats = patientsWithLocation.map(p => p.lat);
    const lngs = patientsWithLocation.map(p => p.lng);
    const latSpread = Math.max(...lats) - Math.min(...lats);
    const lngSpread = Math.max(...lngs) - Math.min(...lngs);
    
    // If spread is large (patients in different cities), zoom out
    if (latSpread > 5 || lngSpread > 5) return 4;
    if (latSpread > 1 || lngSpread > 1) return 8;
    return 12;
  };

  if (loadError) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600">Erro ao carregar Google Maps</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4" data-testid="mapa-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/dashboard')}
              data-testid="btn-voltar-mapa"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Mapa de Pacientes</h1>
              <p className="text-muted-foreground mt-1">
                Visualize a localização dos pacientes e planeje itinerários
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => navigate(-1)}
              className="gap-2"
              data-testid="btn-fechar-mapa"
            >
              Fechar
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 h-[calc(100vh-220px)]">
          {/* Left Panel - Patient List & Controls */}
          <div className="lg:col-span-1 space-y-4 overflow-y-auto">
            {/* Filters */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Filtros</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* View Mode Selector */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium">Colorir por</Label>
                  <Select value={viewMode} onValueChange={setViewMode}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="equipe">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Por Equipe
                        </div>
                      </SelectItem>
                      <SelectItem value="origem">
                        <div className="flex items-center gap-2">
                          <Hospital className="h-4 w-4" />
                          Por Origem
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="include-altas"
                    checked={includeAltas}
                    onCheckedChange={setIncludeAltas}
                  />
                  <Label htmlFor="include-altas" className="text-sm">
                    Incluir Altas Realizadas
                  </Label>
                </div>
                {tecnicos.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="show-tecnicos"
                      checked={showTecnicos}
                      onCheckedChange={setShowTecnicos}
                      data-testid="switch-show-tecnicos"
                    />
                    <Label htmlFor="show-tecnicos" className="text-sm flex items-center gap-1">
                      <Home className="h-3.5 w-3.5" />
                      Casas dos Técnicos
                    </Label>
                  </div>
                )}
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full gap-2"
                  onClick={handleGeocodeAll}
                  disabled={geocodingInProgress}
                >
                  {geocodingInProgress ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <MapPin className="h-4 w-4" />
                  )}
                  Geocodificar Endereços
                </Button>
              </CardContent>
            </Card>

            {/* Legend */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Legenda
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  {legendItems.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full flex-shrink-0" 
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-xs text-muted-foreground truncate">{item.label}</span>
                    </div>
                  ))}
                  {showTecnicos && tecnicos.length > 0 && (
                    <div className="flex items-center gap-2 col-span-2 pt-1 border-t mt-1">
                      <Home className="h-3 w-3 text-orange-500 flex-shrink-0" />
                      <span className="text-xs text-muted-foreground">Casa do Técnico</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Start Point Selector */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Ponto de Partida</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Select value={startPointType} onValueChange={setStartPointType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="base">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Base LifePort
                      </div>
                    </SelectItem>
                    <SelectItem value="location">
                      <div className="flex items-center gap-2">
                        <LocateFixed className="h-4 w-4" />
                        Minha Localização
                      </div>
                    </SelectItem>
                    <SelectItem value="map">
                      <div className="flex items-center gap-2">
                        <MousePointer2 className="h-4 w-4" />
                        Selecionar no Mapa
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>

                {startPointType === 'location' && !userLocation && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full gap-2"
                    onClick={handleGetUserLocation}
                  >
                    <LocateFixed className="h-4 w-4" />
                    Obter Localização
                  </Button>
                )}

                {startPointType === 'map' && (
                  <Button 
                    variant={isSelectingOnMap ? "default" : "outline"}
                    size="sm" 
                    className="w-full gap-2"
                    onClick={() => setIsSelectingOnMap(!isSelectingOnMap)}
                  >
                    <MousePointer2 className="h-4 w-4" />
                    {isSelectingOnMap ? 'Clique no mapa...' : 'Selecionar Ponto'}
                  </Button>
                )}

                <p className="text-xs text-muted-foreground">
                  {startPointType === 'base' && baseLifeport.endereco}
                  {startPointType === 'location' && (userLocation?.endereco || 'Localização não obtida')}
                  {startPointType === 'map' && (customStartPoint?.endereco || 'Nenhum ponto selecionado')}
                </p>
              </CardContent>
            </Card>

            {/* Missing Location Section */}
            {patientsWithoutLocation.length > 0 && (
              <Card className="border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/10">
                <Collapsible open={missingLocationOpen} onOpenChange={setMissingLocationOpen}>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="pb-2 cursor-pointer hover:bg-amber-100/50 dark:hover:bg-amber-900/20 transition-colors">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm flex items-center gap-2 text-amber-700 dark:text-amber-400">
                          <MapPinOff className="h-4 w-4" />
                          Sem localização: {patientsWithoutLocation.length}
                        </CardTitle>
                        {missingLocationOpen ? (
                          <ChevronUp className="h-4 w-4 text-amber-600" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-amber-600" />
                        )}
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="space-y-3">
                      {/* Bulk retry button */}
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full gap-2 border-amber-400 text-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                        onClick={handleRetryGeocodeMissing}
                        disabled={retryingGeocode}
                        data-testid="btn-retry-geocode"
                      >
                        {retryingGeocode ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                        Tentar georreferenciar novamente
                      </Button>
                      
                      {/* Patient list */}
                      <div className="space-y-2 max-h-[250px] overflow-y-auto">
                        {patientsWithoutLocation.map((patient) => (
                          <div
                            key={patient.internacao_id || patient.paciente_id}
                            className="p-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-white dark:bg-background"
                            data-testid={`missing-patient-${patient.paciente_id}`}
                          >
                            <div className="flex-1 min-w-0 mb-2">
                              <p className="font-medium text-sm truncate">{patient.nome}</p>
                              {patient.cpf && (
                                <p className="text-xs text-muted-foreground font-mono">
                                  CPF: {formatCPF(patient.cpf)}
                                </p>
                              )}
                              <p className="text-xs text-amber-600 dark:text-amber-400 truncate mt-1">
                                {patient.endereco || patient.endereco_formatado || 'Endereço não cadastrado'}
                              </p>
                            </div>
                            <div className="flex gap-1.5 flex-wrap">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs gap-1 px-2"
                                onClick={() => handleViewPatient(patient)}
                                data-testid={`btn-ver-paciente-${patient.paciente_id}`}
                              >
                                <Eye className="h-3 w-3" />
                                Ver
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs gap-1 px-2"
                                onClick={() => handleOpenEditAddress(patient)}
                                data-testid={`btn-corrigir-endereco-${patient.paciente_id}`}
                              >
                                <Pencil className="h-3 w-3" />
                                Corrigir
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs gap-1 px-2"
                                onClick={() => handleOpenMapPicker(patient)}
                                data-testid={`btn-definir-mapa-${patient.paciente_id}`}
                              >
                                <MapPin className="h-3 w-3" />
                                Mapa
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            )}

            {/* Patient List */}
            <Card className="flex-1">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">
                    Pacientes ({patientsWithLocation.length})
                  </CardTitle>
                  {patientsWithoutLocation.length > 0 && (
                    <Badge variant="outline" className="text-xs">
                      {patientsWithoutLocation.length} sem localização
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[300px] overflow-y-auto">
                {/* Selecionar Todos */}
                {patientsWithLocation.length > 0 && (
                  <div className="flex items-center justify-between pb-2 mb-2 border-b">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (selectedPatients.length === patientsWithLocation.length) {
                          setSelectedPatients([]);
                        } else {
                          setSelectedPatients([...patientsWithLocation]);
                        }
                      }}
                      className="gap-2"
                    >
                      <Checkbox
                        checked={selectedPatients.length === patientsWithLocation.length && patientsWithLocation.length > 0}
                        className="mr-1"
                      />
                      {selectedPatients.length === patientsWithLocation.length ? 'Desmarcar todos' : 'Selecionar todos'}
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      {selectedPatients.length}/{patientsWithLocation.length}
                    </span>
                  </div>
                )}
                {loading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : patientsWithLocation.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Nenhum paciente com localização
                  </p>
                ) : (
                  patientsWithLocation.map((patient) => {
                    const isSelected = selectedPatients.some(p => p.internacao_id === patient.internacao_id);
                    return (
                      <div
                        key={patient.internacao_id}
                        className={`
                          p-2 rounded-lg border cursor-pointer transition-colors
                          ${isSelected 
                            ? 'border-primary bg-primary/10' 
                            : 'border-border hover:bg-muted/50'
                          }
                        `}
                        onClick={() => handlePatientSelect(patient)}
                      >
                        <div className="flex items-start gap-2">
                          <Checkbox
                            checked={isSelected}
                            className="mt-1"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{patient.nome}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {patient.endereco || patient.endereco_formatado || 'Endereço não disponível'}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                D+{patient.dias_internacao}
                              </Badge>
                              {patient.origem && (
                                <Badge variant="secondary" className="text-xs">
                                  {ORIGEM_LABELS[patient.origem] || patient.origem}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>

            {/* Route Actions */}
            <Card>
              <CardContent className="pt-4 space-y-3">
                <Button 
                  className="w-full gap-2"
                  onClick={handleCalculateRoute}
                  disabled={selectedPatients.length < 1 || routeLoading}
                  data-testid="btn-sugerir-itinerario"
                >
                  {routeLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Route className="h-4 w-4" />
                  )}
                  Sugerir Itinerário ({selectedPatients.length})
                </Button>

                {routeResult && (
                  <>
                    <div className="p-3 bg-muted rounded-lg space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Quilometragem:</span>
                        <span className="font-bold">{routeResult.total_distance_km} km</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Tempo estimado:</span>
                        <span className="font-bold">{routeResult.total_duration_minutes} min</span>
                      </div>
                    </div>

                    <Button 
                      variant="outline"
                      className="w-full gap-2"
                      onClick={handleOpenInGoogleMaps}
                      data-testid="btn-abrir-google-maps"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Abrir no Google Maps
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Optimized Route List */}
            {routeResult && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Ordem de Visitas</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold">
                      P
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-medium">Ponto de Partida</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {getStartPoint().endereco}
                      </p>
                    </div>
                  </div>
                  {routeResult.waypoints.map((wp, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/pacientes/${wp.paciente_id}/internacao/${selectedPatients.find(p => p.paciente_id === wp.paciente_id)?.internacao_id}`)}
                    >
                      <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                        {wp.order}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{wp.paciente_nome}</p>
                        <p className="text-xs text-muted-foreground truncate">{wp.endereco}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Map */}
          <div className="lg:col-span-3 rounded-lg overflow-hidden border">
            {!isLoaded ? (
              <div className="flex items-center justify-center h-full bg-muted">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : (
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={mapCenter}
                zoom={getZoomLevel()}
                onClick={handleMapClick}
                onLoad={(map) => { mapRef.current = map; }}
                options={{
                  zoomControl: true,
                  streetViewControl: false,
                  mapTypeControl: true,
                  fullscreenControl: true,
                }}
              >
                {/* Start Point Marker */}
                {(startPointType !== 'map' || customStartPoint) && (
                  <Marker
                    position={getStartPoint()}
                    icon={{
                      url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png',
                      scaledSize: new window.google.maps.Size(40, 40)
                    }}
                    title="Ponto de Partida"
                  />
                )}

                {/* Patient Markers */}
                {patientsWithLocation.map((patient) => {
                  const isSelected = selectedPatients.some(p => p.internacao_id === patient.internacao_id);
                  const markerColor = getPatientColor(patient);
                  const markerUrl = getGoogleMarkerUrl(markerColor);
                  
                  return (
                    <Marker
                      key={patient.internacao_id}
                      position={{ lat: patient.lat, lng: patient.lng }}
                      icon={{
                        url: isSelected 
                          ? 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png'
                          : markerUrl,
                        scaledSize: new window.google.maps.Size(isSelected ? 45 : 35, isSelected ? 45 : 35)
                      }}
                      onClick={() => {
                        setActiveInfoWindow(patient.internacao_id);
                        handlePatientSelect(patient);
                      }}
                    >
                      {activeInfoWindow === patient.internacao_id && (
                        <InfoWindow onCloseClick={() => setActiveInfoWindow(null)}>
                          <div className="p-1">
                            <p className="font-bold text-sm">{patient.nome}</p>
                            <p className="text-xs text-gray-600">
                              {ORIGEM_LABELS[patient.origem] || patient.origem || 'Origem não informada'}
                            </p>
                            <p className="text-xs">D+{patient.dias_internacao}</p>
                            {patient.equipe_nome && (
                              <p className="text-xs text-gray-600">
                                Equipe: {patient.equipe_nome}
                              </p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              {patient.endereco || patient.endereco_formatado}
                            </p>
                          </div>
                        </InfoWindow>
                      )}
                    </Marker>
                  );
                })}

                {/* Route Polyline */}
                {routeResult?.polyline && (
                  <Polyline
                    path={decodePolyline(routeResult.polyline)}
                    options={{
                      strokeColor: '#2563eb',
                      strokeOpacity: 0.8,
                      strokeWeight: 4,
                    }}
                  />
                )}

                {/* Technician Home Markers */}
                {showTecnicos && tecnicos.map((tec) => (
                  <Marker
                    key={`tec-${tec.equipe_id}`}
                    position={{ lat: tec.lat, lng: tec.lng }}
                    icon={{
                      url: 'https://maps.google.com/mapfiles/ms/icons/orange-dot.png',
                      scaledSize: new window.google.maps.Size(38, 38)
                    }}
                    title={`Casa: ${tec.tecnico_nome} (${tec.equipe_nome})`}
                    onClick={() => setActiveInfoWindow(`tec-${tec.equipe_id}`)}
                  >
                    {activeInfoWindow === `tec-${tec.equipe_id}` && (
                      <InfoWindow onCloseClick={() => setActiveInfoWindow(null)}>
                        <div className="p-1">
                          <p className="font-bold text-sm">{tec.tecnico_nome}</p>
                          <p className="text-xs text-gray-600">Técnico de Enfermagem</p>
                          <p className="text-xs" style={{ color: tec.equipe_cor }}>
                            Equipe: {tec.equipe_nome}
                          </p>
                          {tec.tecnico_telefone && (
                            <p className="text-xs text-gray-500">{tec.tecnico_telefone}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            {tec.tecnico_endereco || 'Endereço definido no mapa'}
                          </p>
                        </div>
                      </InfoWindow>
                    )}
                  </Marker>
                ))}
              </GoogleMap>
            )}
          </div>
        </div>

        {/* Map Selection Overlay */}
        {isSelectingOnMap && (
          <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-primary text-primary-foreground px-6 py-3 rounded-lg shadow-lg z-50">
            <p className="text-sm font-medium">Clique no mapa para selecionar o ponto de partida</p>
          </div>
        )}

        {/* Edit Address Modal */}
        <Dialog open={editAddressModalOpen} onOpenChange={setEditAddressModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="h-5 w-5" />
                Corrigir Endereço
              </DialogTitle>
              <DialogDescription>
                {selectedMissingPatient?.nome}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Logradouro</Label>
                <Input
                  value={editAddressData.logradouro}
                  onChange={(e) => setEditAddressData({...editAddressData, logradouro: e.target.value})}
                  placeholder="Rua, Avenida, etc."
                  data-testid="input-logradouro"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-2 col-span-1">
                  <Label>Número</Label>
                  <Input
                    value={editAddressData.numero}
                    onChange={(e) => setEditAddressData({...editAddressData, numero: e.target.value})}
                    placeholder="123"
                    data-testid="input-numero"
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Bairro</Label>
                  <Input
                    value={editAddressData.bairro}
                    onChange={(e) => setEditAddressData({...editAddressData, bairro: e.target.value})}
                    placeholder="Bairro"
                    data-testid="input-bairro"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-2 col-span-2">
                  <Label>Cidade</Label>
                  <Input
                    value={editAddressData.cidade}
                    onChange={(e) => setEditAddressData({...editAddressData, cidade: e.target.value})}
                    placeholder="Cidade"
                    data-testid="input-cidade"
                  />
                </div>
                <div className="space-y-2">
                  <Label>UF</Label>
                  <Input
                    value={editAddressData.uf}
                    onChange={(e) => setEditAddressData({...editAddressData, uf: e.target.value.toUpperCase()})}
                    placeholder="RR"
                    maxLength={2}
                    data-testid="input-uf"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>CEP</Label>
                <Input
                  value={editAddressData.cep}
                  onChange={(e) => setEditAddressData({...editAddressData, cep: e.target.value})}
                  placeholder="00000-000"
                  data-testid="input-cep"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Após salvar, o sistema tentará localizar o endereço automaticamente.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditAddressModalOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveAddress} 
                disabled={savingAddress || !editAddressData.logradouro}
                data-testid="btn-salvar-endereco"
              >
                {savingAddress ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Salvar e Geocodificar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Map Picker Modal */}
        <MapPicker
          open={mapPickerOpen}
          onOpenChange={setMapPickerOpen}
          initialPosition={{
            lat: baseLifeport.lat,
            lng: baseLifeport.lng
          }}
          onSelect={handleMapPickerSelect}
          title={`Definir localização: ${selectedMissingPatient?.nome || ''}`}
        />
      </div>
    </Layout>
  );
};

export default Mapa;
